import { useMemo, useState, type ReactElement } from 'react'
import { isEmpty, searchIncludes, type CompanyId, type RoleData } from '@terros/common'
import { Modal, ScrollArea, Stack, Text, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import { Size, useRoles, useTranslation } from '@front/model'
import type { DashboardScopeTarget } from './types'
import { DashboardScopeOptionButton } from './DashboardScopeOptionButton'

type DashboardRolePickerModalProps = {
  companyId?: CompanyId
  opened: boolean
  onClose: () => void
  onSelect: (target: DashboardScopeTarget) => void
}

export const DashboardRolePickerModal = ({
  companyId,
  opened,
  onClose,
  onSelect,
}: DashboardRolePickerModalProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { data: roles = [], isLoading, error } = useRoles({ companyId, showArchived: false, enabled: opened })
  useToastError(error)
  const filteredRoles = useMemo(() => {
    const query = search.trim()
    if (isEmpty(query)) return roles
    return roles.filter((role) => searchIncludes([role.name], query))
  }, [roles, search])

  const handleClose = (): void => {
    setSearch('')
    onClose()
  }

  const handleSelect = (role: RoleData): void => {
    setSearch('')
    onSelect({ entityId: role.roleId, name: role.name, kind: 'role' })
  }

  return (
    <Modal opened={opened} title={t('Roles')} onClose={handleClose}>
      <Stack gap='sm'>
        <TextInput
          value={search}
          placeholder={t('Search roles')}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <ScrollArea.Autosize mah={360}>
          <Stack gap={Size.gap.list}>
            {filteredRoles.map((role) => (
              <DashboardScopeOptionButton key={role.roleId} onClick={() => handleSelect(role)}>
                {role.name}
              </DashboardScopeOptionButton>
            ))}
            {isLoading && <Text c='dimmed'>{t('Loading...')}</Text>}
            {!isLoading && isEmpty(filteredRoles) && <Text c='dimmed'>{t('No roles found')}</Text>}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  )
}
